import styled from '@emotion/styled'

export const PageContainer = styled.div`
  background: #ffffff;
  color: #1f2937;
  width: 100%;
  min-width: 0;
`

export const MainContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 32px 40px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 12px;
    min-width: 0;
  }
`

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`

export const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ef4444;
`

export const ErrorText = styled.div`
  font-size: 16px;
  color: #ef4444;
  text-align: center;
  max-width: 400px;
`

export const RetryButton = styled.button`
  padding: 12px 24px;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }
`

export const PageHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 16px;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

export const PageCount = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #6b7280;
  margin: 0;
`

export const SchoolSection = styled.section`
  margin-bottom: 40px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

export const SchoolHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`

export const SchoolName = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`

export const PeriodSection = styled.section`
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

export const PeriodHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 12px;
`

export const PeriodName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
`

export const GroupCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

export const EmptyIcon = styled.div`
  font-size: 64px;
`

export const EmptyTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

export const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  max-width: 400px;
  margin: 0;
  line-height: 1.5;
`

export const EnrollmentList = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @media (min-width: 769px) {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 480px), 1fr));
  }
`

export const EnrollmentCard = styled.div`
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.15);
  }
`

export const CardTopRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 12px;
  margin-bottom: 8px;
`

export const CardStatusPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
`

export const StatusPill = styled.div<{ $status: string; $prominent?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $prominent }) => ($prominent ? '8px' : '6px')};
  padding: ${({ $prominent }) => ($prominent ? '8px 16px' : '6px 12px')};
  border-radius: 999px;
  font-size: ${({ $prominent }) => ($prominent ? '14px' : '12px')};
  font-weight: ${({ $prominent }) => ($prominent ? 700 : 600)};
  flex-shrink: 0;

  &::before {
    content: '';
    width: ${({ $prominent }) => ($prominent ? '10px' : '8px')};
    height: ${({ $prominent }) => ($prominent ? '10px' : '8px')};
    border-radius: 50%;
    background: currentColor;
    opacity: 0.75;
  }

  ${({ $status }) => {
    switch ($status) {
      case 'confirmed':
      case 'active':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        `
      case 'paid':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        `
      case 'unpaid':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        `
      case 'partlyPaid':
      case 'pending':
      case 'partlyApproved':
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #d97706;
        `
      case 'cancelledWithRefund':
      case 'cancelledWithCredit':
      case 'refunded':
      case 'partlyRefunded':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        `
      default:
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #6b7280;
        `
    }
  }}
`

export const CardTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #111827;
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`

export const SchoolSubtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  line-height: 1.35;
`

export const CardHeroRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  margin-top: 4px;
`

export const CardMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
`

export const QrThumbColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media (min-width: 400px) {
    margin-left: auto;
  }
`

export const QrThumbButton = styled.button`
  display: block;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid var(--portal-primary, #7c3aed);
    outline-offset: 2px;
  }
`

export const QrThumbInner = styled.div`
  background: #ffffff;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  line-height: 0;
  color-scheme: light only;
  isolation: isolate;
  forced-color-adjust: none;

  & img {
    display: block;
    forced-color-adjust: none;
  }
`

export const QrThumbPlaceholder = styled.div`
  width: 84px;
  height: 84px;
  background: #ffffff;
`

export const TapForQrButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 999px;
  background: var(--portal-primary-surface, #f5f3ff);
  color: var(--portal-primary, #7c3aed);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--portal-primary-surface-strong, #ede9fe);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.35);
  }
`

export const DetailOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
    align-items: flex-end;
  }
`

export const DetailCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  max-width: min(960px, 100%);
  width: 100%;
  max-height: 90vh;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  box-sizing: border-box;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
  position: relative;

  @media (max-width: 768px) {
    max-height: calc(100vh - 24px);
    border-radius: 16px 16px 12px 12px;
  }
`

export const DetailColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  min-width: 0;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.85fr);
    align-items: start;
  }
`

export const DetailMainColumn = styled.div`
  min-width: 0;
`

export const DetailSideColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 768px) {
    position: sticky;
    top: 0;
  }
`

export const DetailCardCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 999px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition:
    background 0.2s,
    color 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  &:focus-visible {
    outline: 2px solid var(--portal-primary, #7c3aed);
    outline-offset: 2px;
  }
`

export const DetailCardContent = styled.div`
  padding: 24px;
  padding-top: 56px;
  padding-right: 28px;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px;
    padding-top: 52px;
    padding-right: 20px;
  }
`

export const DetailTopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding-right: 44px;
  min-width: 0;
`

export const DetailSchoolSubtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  text-align: left;
  margin: -8px 0 16px 0;
  min-width: 0;
  overflow-wrap: anywhere;
`

export const DetailCardTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 16px 0;
  overflow-wrap: anywhere;
`

export const DetailQRSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
`

export const DetailQRWrapper = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  color-scheme: light only;
  isolation: isolate;
  forced-color-adjust: none;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  & img {
    display: block;
    forced-color-adjust: none;
  }
`

export const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 8px;
`

export const DetailSection = styled.div`
  margin-top: 20px;
`

export const DetailSideSection = styled.div`
  margin-top: 0;
`

export const DescriptionText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
`

export const OpenDescriptionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: var(--portal-primary, #7c3aed);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s;

  &:hover {
    background: var(--portal-primary-surface, #f5f3ff);
    border-color: var(--portal-primary, #7c3aed);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.35);
  }
`

export const DescriptionOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
`

export const DescriptionModal = styled.div`
  background: #ffffff;
  border-radius: 16px;
  max-width: min(90vw, 720px);
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  position: relative;
  padding: 24px;
  padding-top: 56px;
  box-sizing: border-box;
`

export const DescriptionModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 16px 0;
`

export const RichDescription = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: #374151;
  overflow-x: auto;

  p {
    margin: 0 0 12px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 12px 0;
    border-radius: 8px;
  }

  table {
    border-collapse: collapse;
    max-width: 100%;
    width: 100%;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 12px 0;
  }

  td,
  th {
    word-break: break-word;
  }

  ul,
  ol {
    margin: 0 0 12px 0;
    padding-left: 1.25em;
  }

  a {
    color: var(--portal-primary, #7c3aed);
  }
`

export const LocationText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`

export const OccasionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const OccasionRow = styled.li<{ $cancelled?: boolean }>`
  font-size: 14px;
  color: ${({ $cancelled }) => ($cancelled ? '#9ca3af' : '#111827')};
  text-decoration: ${({ $cancelled }) => ($cancelled ? 'line-through' : 'none')};
`

export const ReceiptBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
`

export const ReceiptRef = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 12px;
`

export const ReceiptSellerBlock = styled.div`
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
`

export const ReceiptSellerLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
`

export const ReceiptSellerValue = styled.div`
  font-size: 14px;
  color: #111827;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const ReceiptRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const ReceiptRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  min-width: 0;
`

export const ReceiptRowLabel = styled.span`
  font-weight: 500;
  color: #6b7280;
  min-width: 0;
  overflow-wrap: anywhere;
`

export const ReceiptRowValue = styled.span<{ $tone?: 'paid' | 'remaining' }>`
  font-weight: 600;
  color: ${({ $tone }) => ($tone === 'paid' ? '#16a34a' : $tone === 'remaining' ? '#d97706' : '#111827')};
  text-align: right;
  flex-shrink: 0;
`

export const ReceiptActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`

export const SsnToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`

export const DownloadPdfButton = styled.button`
  padding: 8px 16px;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }
`

export const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const ReceiptsSection = styled.section`
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`

export const ReceiptsSectionHeader = styled.div`
  margin-bottom: 12px;
`

export const ReceiptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const ReceiptListRow = styled.div<{ $expanded?: boolean }>`
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
  padding: 14px 16px;
  border: 1px dashed ${({ $expanded }) => ($expanded ? 'var(--portal-primary, #7c3aed)' : '#cbd5e1')};
  border-radius: 10px;
  background: #f8fafc;
  min-width: 0;
  transition:
    border-color 0.2s,
    background 0.2s;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    background: #f1f5f9;
  }
`

export const ActivityListRow = styled.div<{ $expanded?: boolean }>`
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
  padding: 12px 16px;
  border: 1px solid ${({ $expanded }) => ($expanded ? 'var(--portal-primary, #7c3aed)' : '#e5e7eb')};
  border-radius: 12px;
  background: #ffffff;
  min-width: 0;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    box-shadow: 0 4px 12px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.12);
  }
`

export const ActivityHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

export const ActivityCollapseButton = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;

  &:focus-visible {
    outline: 2px solid var(--portal-primary, #7c3aed);
    outline-offset: 4px;
    border-radius: 8px;
  }
`

export const ActivityCollapseCopy = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`

export const ActivityStatusPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`

export const ActivityCollapsedSummary = styled.div`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.4;
  color: #6b7280;
`

export const ActivityExpandIcon = styled.span<{ $expanded?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: #6b7280;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`

export const ActivityExternalLink = styled.a`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: #6b7280;
  text-decoration: none;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  &:focus-visible {
    outline: 2px solid var(--portal-primary, #7c3aed);
    outline-offset: 2px;
  }
`

export const ActivityExpandedDetails = styled.div`
  margin-top: 12px;
`

export const ActivityListTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  overflow-wrap: anywhere;
`

export const ActivityMetaGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px 16px;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

export const ActivityMetaItem = styled.div`
  min-width: 0;
`

export const ActivityMetaValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`

export const ActivityRowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`

export const PayEnrollmentLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: var(--portal-primary, #7c3aed);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  box-sizing: border-box;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }

  &:focus-visible {
    outline: 2px solid var(--portal-primary, #7c3aed);
    outline-offset: 2px;
  }
`

export const PayEnrollmentLinkCompact = styled(PayEnrollmentLink)`
  align-self: flex-start;
  width: auto;
  margin-top: 6px;
  padding: 12px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  background: #fecaca;
  color: #991b1b;

  &:hover {
    background: #fca5a5;
  }

  &:focus-visible {
    outline-color: #dc2626;
  }
`

export const PayEnrollmentLinkBlock = styled(PayEnrollmentLink)`
  width: 100%;
  margin-top: 12px;
`

export const DetailCloseFooter = styled.button`
  width: 100%;
  margin-top: 16px;
  padding: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`
